// src/components/mfa/RecoveryCodesPanel.jsx
//
// One-time display of recovery codes, after setup and after "New recovery
// codes". The codes exist in this component's props only: they are not stored,
// not logged, and gone once the dialog closes. Closing needs the acknowledgement
// box, as with BICredentialsModal.
import React, { useState } from 'react';
import { AlertTriangle, Check, Copy, Download, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import MfaModalShell from './MfaModalShell';
import { recoveryCodesText } from '../../lib/mfa';

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function RecoveryCodesPanel({ codes, account }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard write failed', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-200">
          <strong className="font-semibold">Save these codes now.</strong>
          {' '}
          This is the only time they will be shown. Each one signs you in once if
          your authenticator app is not at hand.
        </p>
      </div>

      <ul
        className="grid grid-cols-2 gap-2 p-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg"
        aria-label="Recovery codes"
      >
        {codes.map((code) => (
          <li key={code} className="font-mono text-sm text-white tracking-wide">{code}</li>
        ))}
      </ul>

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={handleCopy}>
          {copied ? (
            <><Check className="h-4 w-4 mr-2 text-green-400" />Copied</>
          ) : (
            <><Copy className="h-4 w-4 mr-2" />Copy all</>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => downloadText('connex-recovery-codes.txt', recoveryCodesText(codes, account))}
        >
          <Download className="h-4 w-4 mr-2" />
          Download .txt
        </Button>
      </div>
    </div>
  );
}

export function AcknowledgeAndClose({ acknowledged, onAcknowledge, onClose, label = 'Done' }) {
  return (
    <>
      <label className="flex items-start gap-3 cursor-pointer select-none mb-4">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => onAcknowledge(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[#2a2a2a] bg-[#1a1a1a] text-blue-500 focus:ring-blue-500 cursor-pointer"
        />
        <span className="text-sm text-white">
          I have saved my recovery codes somewhere safe. I understand they will
          not be shown again.
        </span>
      </label>
      <div className="flex justify-end">
        <Button onClick={onClose} disabled={!acknowledged} variant={acknowledged ? 'default' : 'ghost'}>
          {label}
        </Button>
      </div>
    </>
  );
}

export default function RecoveryCodesModal({ codes, account, onClose }) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <MfaModalShell
      icon={KeyRound}
      tone="green"
      title="New recovery codes"
      subtitle="Your old recovery codes no longer work."
      footer={<AcknowledgeAndClose acknowledged={acknowledged} onAcknowledge={setAcknowledged} onClose={onClose} />}
    >
      <RecoveryCodesPanel codes={codes} account={account} />
    </MfaModalShell>
  );
}
